/**
 * MAP-IMPORT-MODAL
 * Import plan for the establishment
 */
import Modal from "@/components/modal"
import { store, useAppDispatch, useAppSelector } from "@/core/root-reducer"
import LoadingModal from "@/modals/loading.modal"
import UploadButtonWidget from "@/widgets/upload-button.widget"
import { useEffect, useState } from "react"
import mapStore from "../../model/map.store"
import { upload } from "@/redux/_upload.actions"
import fileToImage from "@/utils/file-to-image"
import TextInput from "@/components/text-input"
import { createEmptyMap } from "../../model/map.types"
import ModalConfirm from "@/components/modal-confirm"
import { t } from "i18next"
import { refreshMapImageVersion } from "../../model/map-image-cache.utils"

interface Props {
  onClose: () => void
}

export default function MapEditModal(props: Props) {
  const dispatch = useAppDispatch()

  //Selector
  const draftMap = useAppSelector(mapStore.selectors.draft)
  const isEdited = useAppSelector(mapStore.selectors.isEdited)
  const maps = useAppSelector(mapStore.selectors.list)

  //Existing item
  const exists = draftMap.EstablishmentId !== ""

  //State
  const [modal, setModal] = useState<null | "MODAL_LOADING" | "MODAL_REMOVE">(null)

  //Set map order and name on load for new plan
  useEffect(() => {
    if (!exists) {
      dispatch(
        mapStore.actions.replace(
          createEmptyMap({ order: maps.length, name: maps.length === 0 ? "RDC" : "Niveau " + maps.length })
        )
      )
    }
  }, [])

  //Remove plan
  async function remove() {
    setModal("MODAL_LOADING")
    const response = await dispatch(mapStore.thunks.removeEntity(draftMap.id))
    if (response?.type !== "map/remove/rejected") {
      props.onClose()
    } else {
      setModal(null)
    }
  }

  //Save modifications
  async function save() {
    setModal("MODAL_LOADING")
    const response = await dispatch(mapStore.thunks.updateEntity()).unwrap()
    if (response) {
      props.onClose()
    } else {
      setModal(null)
    }
  }

  //Upload the file
  //Create the plan
  async function uploadFile(files: File[]) {
    if (!files.length) return
    const image = await fileToImage(files[0])
    setModal("MODAL_LOADING")
    const response: any = await store.dispatch(upload(draftMap.id, image, "Maps", "image/png"))

    //Handle the response
    if (response === "OK") {
      refreshMapImageVersion(draftMap.id)
      if (exists) {
        dispatch(mapStore.actions.replace({ ...draftMap }))
        props.onClose()
      } else {
        const response = await dispatch(mapStore.thunks.createEntity(draftMap)).unwrap()
        if (response) {
          dispatch(mapStore.actions.replace({ ...response }))
          props.onClose()
        }
      }

      setModal(null)
    } else {
      setModal(null)
    }
  }

  return (
    <Modal
      onDelete={exists ? () => setModal("MODAL_REMOVE") : undefined}
      onClose={() => props.onClose()}
      title={exists ? "Mettre à jour un niveau" : "Importer un niveau"}
      isCloseButtonVisible
      onNext={isEdited && exists ? save : undefined}>
      {modal === "MODAL_LOADING" && <LoadingModal />}
      {modal === "MODAL_REMOVE" && (
        <ModalConfirm
          onNo={() => setModal(null)}
          onYes={remove}
          textBold={t("utils_next_ask")}
          text={`Vous êtes sur le point de supprimer le niveau « ${draftMap.name} ».`}
        />
      )}

      <TextInput
        title="Nom du niveau"
        value={draftMap.name}
        onChange={(e) => dispatch(mapStore.actions.edit({ key: "name", value: e.value }))}
      />
      <div className="-m-[3px] mt-20">
        <UploadButtonWidget acceptedFiles="image/png" onUpload={(e) => uploadFile(e)} />
      </div>
    </Modal>
  )
}
